import React, { useState, useContext } from 'react';
import { Timeline, TimelineItem, TimelineDot, TimelineSeparator, TimelineConnector, TimelineOppositeContent, TimelineContent } from '@mui/lab';
import { Paper, CircularProgress, Stack, Box, Link, Button, Select, MenuItem, InputLabel, FormControl, Checkbox, ListItemText } from '@mui/material';
import { HighlightOff as HighlightOffIcon, CheckCircleOutline as CheckCircleOutlineIcon, AvTimerSharp as AvTimerSharpIcon } from '@mui/icons-material';

import { makeStyles } from '@mui/styles';
import StraightIcon from '@mui/icons-material/Straight';

import GamesContext, { GamesContextType } from '../../store/games-context';
import RunsContext, { RunContextType } from '../../store/runs-context';
import ColorModeContext from '../../store/color-context';

import ResultModal from '../../components/Results/ResultsModal';

import Run from '../../types/run';
import appConfig from '../../config/app.config';

import { STATUS } from '../../utils/constants';
import { groupByDateAcc, getDistancePB, stringDateToDate, getRunStatus } from '../../utils/utils';

import timelineStyles from './timeline.module.scss';

const useStyles = makeStyles((theme) =>  {
return {
  timeline: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  paper: {
    padding: '6px 16px',
    backgroundColor: theme.palette.mode === 'light' ? theme.palette.primary.light : theme.palette.background.default,
    color: theme.palette.common.white,
    cursor: 'pointer',
  },
  succeeded: {
    color: theme.palette.mode === 'light' ? '#6f6' : '#3c3',
    content: 'OK',
  },
  failed: {
    color: theme.palette.mode === 'light' ? '#C33' : '#F33',
    content: 'KO',
  },
  notended: {
    color: theme.palette.mode === 'light' ? '#FFF' : '#CCC',
  },
  run: {
    padding: '5px 0',
    display: 'flex',
    verticalAlign: 'middle'
  },
  date: {
    fontSize: '20px'
  },
  loading: {
    width: '40px',
    height: '40px',
    margin: '0 auto',
    display: 'block',
  },
  clipLink: {
    color: '#FFF'
  },
  game: {
    verticalAlign: 'middle',
    width: '100px',
    display: 'inline-block'
  },
  gameAcronym: {
    verticalAlign: 'middle',
    width: '25px',
    display: 'inline-block'
  },
  icon: {
    verticalAlign: 'middle',
    margin: '0 5px',
    display: 'inline-block'
  },
  button: {
    cursor: 'pointer'
  },
  buttonReverse: {
    cursor: 'pointer',
    transform: 'rotate(180deg)'
  },
  orderButton: {
    margin: '0 10px'
  },
  runBlock: {
    display: 'flex',
    flexDirection: 'column'
  },
  runBlockReverse: {
    display: 'flex',
    flexDirection: 'column-reverse'
  },
  reset: {
    fontWeight: 'bold',
    display: 'flex',
    padding: '0 5px 0 0'
  },
  resetInverse: {
    fontWeight: 'bold',
    display: 'flex',
    padding: '0 5px 0 0'
  }
};
});


const TimelineComponent = () => {
  // getModalStyle is not a pure function, we roll the style only on the first render
  const gamesContext = useContext<GamesContextType>(GamesContext);
  const runsContext = useContext<RunContextType>(RunsContext);
  const colorModeContext = useContext(ColorModeContext);
  const [gamesToFilter, setGamesToFilter] = useState<number[]>([] as number[]);

  const games = gamesContext.games;
  const runs = runsContext.runs;
  const colorMode = colorModeContext.colorMode;

  const [dateRuns, setDateRuns] = useState<Run[]>([]);
  const [recentFirst, setRecentFirst] = useState<boolean>(true);
  const [hasToShowResults, showResults] = useState(false);
  const classes = useStyles();
  
  const displayDayResults = (dateRuns: Run[]) => {
    setDateRuns(dateRuns);
    showResults(true);
  };

  const displayResult = () => {
    return (
      <ResultModal runs={dateRuns} onClose={() => showResults(false)} hasToShowResults={hasToShowResults} />
    );
  };

  const displayRun = (run: Run) => {
    const runStatus = getRunStatus(run, games);
    const selectedRunGame = games[run.selectedGame];
    return (
      <div className={`${recentFirst ? classes.runBlockReverse : classes.runBlock}`}>      
      {run.reset ? 
        <span className={`${recentFirst ? classes.reset : classes.resetInverse}`}>
        (RESET)
        </span>
        :
        ""
      }
      <div className={classes.run} key={`${run.date}-${run.order}-${run._id}`} > 
        <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'inline-block' }}} >
          <span className={classes.game}>
          {selectedRunGame?.name}
          </span>
          <span className={`${(classes as any)[runStatus.toLowerCase()]} ${classes.icon}`}>
            {
              (runStatus === STATUS.FAILED ? <HighlightOffIcon/> : (runStatus === STATUS.SUCCEEDED ? <CheckCircleOutlineIcon/> : <AvTimerSharpIcon/>))
            }
          </span>
        </Box>
        <Box sx={{ flexGrow: 1, display: { xs: 'inline-block', sm: 'none' }}} >
          <span className={classes.gameAcronym}>
            {selectedRunGame?.acronym}
          </span>
          <span className={`${(classes as any)[runStatus.toLowerCase()]} ${classes.icon}`}>
            {
              (runStatus === STATUS.FAILED ? <HighlightOffIcon/> : (runStatus === STATUS.SUCCEEDED ? <CheckCircleOutlineIcon/> : null))
            }
          </span>
      </Box>
    </div>
    </div>
    );
  }
  const displayTimelineItem = (dateRuns: Run[] = []) => {
    const sortedRuns = dateRuns.sort((a, b) => recentFirst ? (b.order - a.order) : (a.order - b.order) );
    const firstRun = sortedRuns?.[0];
    const date = firstRun?.date;
    if (!date) {
      return (<></>);
    }
    const dateFromString = stringDateToDate(date);
    const timelineItemDate = dateFromString.toLocaleDateString();
    return (
      <TimelineItem key={timelineItemDate}>
        <TimelineOppositeContent>
          <div color="textSecondary" className={classes.date}>{timelineItemDate}</div>
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot />
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <Paper elevation={10} className={classes.paper} onClick={() => (displayDayResults(sortedRuns))}>
            {
              sortedRuns.map(run => { return displayRun(run); })
            }
          </Paper>
        </TimelineContent>
      </TimelineItem>
    )
  };

  if (runs?.length > 0 && games?.length > 0) {
    const clonedRuns = [...runs];
    const filteredRuns = gamesToFilter.length > 0 ? clonedRuns.filter(run => gamesToFilter.includes(run.selectedGame)) : clonedRuns;
    const distancePBRun = getDistancePB(clonedRuns, games);
    const orderedRuns = filteredRuns.sort((runA, runB) => {
      const runATime = (new Date(runA.date).getTime());
      const runBTime = (new Date(runB.date).getTime());
      return runATime - runBTime;
    });
    const timelineRuns = JSON.parse(JSON.stringify(orderedRuns)) as Run[];
    if(recentFirst) {
      timelineRuns.reverse();
    }
    const groupedRuns = timelineRuns.reduce(groupByDateAcc, []);
    const groupedRunsForNumberOfStreams = clonedRuns.reduce(groupByDateAcc, []);
    const sortedRuns = groupedRuns;
    const initialDate = clonedRuns && clonedRuns[0] ? new Date(clonedRuns[0].date) : new Date();
    const today = new Date();
    const difference = today.getTime() - initialDate.getTime();
    const daysBetweenTodayAndInitialDate = Math.ceil(difference / (1000 * 3600 * 24));
    const numberOfStreams = groupedRunsForNumberOfStreams.length;
    
    return (
      <>
        <h2 className={timelineStyles.header}><Link href={`https://twitch.tv/${appConfig.streamer}`}>{appConfig.streamerDisplayText}</Link>'s {appConfig.run} started {daysBetweenTodayAndInitialDate} days ago and includes {numberOfStreams} streams</h2>
        <h3 className={timelineStyles.header}>Distance PB: <Link href={distancePBRun.clip}>Game: {distancePBRun.gameNumber}, Split: {distancePBRun.splitName} </Link></h3>
        <h4 className={timelineStyles.header}>
          <div className={timelineStyles.alignedContainer}>
            <div className={timelineStyles.alignedSpan}>
              <span className={timelineStyles.alignedSpan}>Run Sorted: </span>
              <Button className={classes.orderButton} variant='outlined' onClick={() => setRecentFirst(!recentFirst)} ><StraightIcon className={`${classes.button} ${!recentFirst ? classes.buttonReverse : '' } `}/>{recentFirst ? 'recent first' : 'older first'}</Button>
            </div>
            <div className={timelineStyles.alignedSpan}>
              <FormControl  sx={{ m: 1, minWidth: 210 }}>
              <InputLabel id="game-select-label">Filter Games</InputLabel>
              <Select
                id="demo-multiple-name"
                labelId="game-select-label"
                multiple
                value={gamesToFilter}
                renderValue={(selected) => {
                  const renderValue = selected.map(game => {
                    return games[game].acronym
                    ;
                  });
                  return renderValue.join(', ');
                }}
                label="Games"
                onChange={(e) => {
                  setGamesToFilter(e.target.value as number[]);
                }}
              >
                {games.map((game) => (
                <MenuItem
                  key={game.acronym}
                  value={game.id}
                >
                  <Checkbox checked={gamesToFilter.indexOf(game.id) > -1} />
                  <ListItemText primary={game.name} />
                </MenuItem>
                ))}</Select>
              </FormControl>
            </div>
          </div>
        </h4>
        <Timeline position="alternate" className={classes.timeline}>
          {(sortedRuns.map(run => { return displayTimelineItem(run.runs) }))}
        </Timeline>
        {displayResult()}
      </>
    )
  }
  return (
    <div className={classes.loading}>
      <CircularProgress />
    </div>
  );
};
export default TimelineComponent;
